<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import LoadingElement from "@/components/Animations/LoadingElement.vue";
import TopDeck from "@/components/Utils/TopDeck.vue";
import { useAuthStore } from "@/store/auth_store";
import {
  getUserIngredients,
  deleteIngredientFromUserCollection,
  setIngredientAsFavorite,
  updateProduct,
} from "@/service/ingredient_service";
import EditIngredientModal from './EditIngredientModal.vue'
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import AccountToggle from "@/components/Utils/AccountToggle.vue";

export default {
  name: "MyIngredients",
  components: { LoadingElement, TopDeck, ThemeToggle, AccountToggle, EditIngredientModal },
  setup() {
    const authStore = useAuthStore();
    const errorMessage = ref("");
    const snackbar = ref({ show: false, text: "", color: "success" });
    const router = useRouter();

    const WINTER = new Set([11, 0, 1, 2]);
    const SUMMER = new Set([5, 6, 7, 8]);
    function getSeasonIcon(months) {
      if (!Array.isArray(months) || !months.length) return '';
      const hasWinter = months.some(m => WINTER.has(m));
      const hasSummer = months.some(m => SUMMER.has(m));
      if (hasWinter && hasSummer) return '❄️☀️';
      if (hasWinter) return '❄️';
      if (hasSummer) return '☀️';
      return '🌿';
    }

    const ingredients = ref([]);
    const isLoadingIngredients = ref(false);
    const selectedIngredient = ref(null);
    const searchQuery = ref("");

    const selectedCategory = ref("Tous");
    const editModalOpen = ref(false);
    const seasonFilter = ref(null); // null | 'winter' | 'summer' | 'frozen'
    
    // Modal de confirmation
    const deleteDialog = ref(false);
    const ingredientToDelete = ref(null);

    // Filtres de tri
    const sortBy = ref(null);
    const sortKey = ref(null);
    const sortDir = ref('asc');

    function setSort(key) {
      if (sortKey.value === key) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey.value = key;
        sortDir.value = 'asc';
      }
    }

    function nutriscoreColor(v) {
      const map = { A: 'green', B: 'light-green', C: 'yellow-darken-3', D: 'orange', E: 'red' };
      return map[v] || 'grey';
    }

    function getKcal(item) {
      if (!item.nutriments_map) return '—';
      const entry = item.nutriments_map.find(n =>
        n.key === 'energy-kcal_100g' || n.key === 'energy_100g' || (n.key || '').toLowerCase().includes('kcal')
      );
      return entry ? entry.value : '—';
    }

    const CATEGORY_META = {
      'légumes':      { emoji: '🥦', label: 'Légumes' },
      'fruits':       { emoji: '🍎', label: 'Fruits' },
      'poissons':     { emoji: '🐟', label: 'Poissons' },
      'légumineuses': { emoji: '🫘', label: 'Légumineuses' },
      'huiles':       { emoji: '🫙', label: 'Huiles' },
      'farines':      { emoji: '🌾', label: 'Farines' },
      'épices':       { emoji: '🌶️', label: 'Épices' },
      'viandes':      { emoji: '🥩', label: 'Viandes' },
      'céréales':     { emoji: '🌾', label: 'Céréales' },
      'produits laitiers': { emoji: '🥛', label: 'Laitiers' },
      'œufs':         { emoji: '🥚', label: 'Œufs' },
    };

    const categories = computed(() => {
      const uniqueCategories = new Set(
        ingredients.value
          .map((item) => (item.category ?? "").trim())
          .filter((cat) => cat.length > 0)
      );

      return ["Tous", ...Array.from(uniqueCategories).sort()];
    });

    const categoryCards = computed(() => {
      return categories.value
        .filter(c => c !== "Tous")
        .map(cat => {
          const key = cat.toLowerCase().trim();
          const meta = Object.entries(CATEGORY_META).find(([k]) => key.includes(k));
          const count = ingredients.value.filter(i => (i.category ?? "").toLowerCase() === key).length;
          return {
            key: cat,
            label: meta ? meta[1].label : cat,
            emoji: meta ? meta[1].emoji : '🍽️',
            count,
          };
        });
    });

    const filteredIngredients = computed(() => {
      const q = searchQuery.value.trim().toLowerCase();

      let filtered = ingredients.value.filter((item) => {
        const matchesSearch = ["productName", "productBrand", "category"].some(
          (key) => (item[key] ?? "").toLowerCase().includes(q)
        );
        const matchesCategory =
          selectedCategory.value === "Tous" ||
          (item.category ?? "").toLowerCase() === selectedCategory.value.toLowerCase();

        let matchesSeason = true;
        if (seasonFilter.value === 'winter') {
          matchesSeason = Array.isArray(item.seasonMonths) && item.seasonMonths.some(m => WINTER.has(m));
        } else if (seasonFilter.value === 'summer') {
          matchesSeason = Array.isArray(item.seasonMonths) && item.seasonMonths.some(m => SUMMER.has(m));
        } else if (seasonFilter.value === 'frozen') {
          matchesSeason = item.isFrozen === true;
        }

        return matchesSearch && matchesCategory && matchesSeason;
      });

      // Tri par colonne (flèches)
      if (sortKey.value) {
        filtered = [...filtered].sort((a, b) => {
          if (sortKey.value === 'name') {
            return sortDir.value === 'asc'
              ? (a.productName || '').localeCompare(b.productName || '')
              : (b.productName || '').localeCompare(a.productName || '');
          }
          if (sortKey.value === 'category') {
            return sortDir.value === 'asc'
              ? (a.category || '').localeCompare(b.category || '')
              : (b.category || '').localeCompare(a.category || '');
          }
          if (sortKey.value === 'nutriscore') {
            return sortDir.value === 'asc'
              ? (a.nutriscore || '').localeCompare(b.nutriscore || '')
              : (b.nutriscore || '').localeCompare(a.nutriscore || '');
          }
          if (sortKey.value === 'kcal') {
            const aVal = parseFloat(getKcal(a)) || 0;
            const bVal = parseFloat(getKcal(b)) || 0;
            return sortDir.value === 'asc' ? aVal - bVal : bVal - aVal;
          }
          return 0;
        });
      }

      // Tri legacy (sortBy)
      if (sortBy.value) {
        filtered = [...filtered].sort((a, b) => {
          switch (sortBy.value) {
            case 'name-asc':
              return (a.productName || '').localeCompare(b.productName || '');
            case 'name-desc':
              return (b.productName || '').localeCompare(a.productName || '');
            case 'brand-asc':
              return (a.productBrand || '').localeCompare(b.productBrand || '');
            case 'brand-desc':
              return (b.productBrand || '').localeCompare(a.productBrand || '');
            case 'category-asc':
              return (a.category || '').localeCompare(b.category || '');
            case 'category-desc':
              return (b.category || '').localeCompare(a.category || '');
            case 'portion-asc':
              return (a.servingQuantity || 0) - (b.servingQuantity || 0);
            case 'portion-desc':
              return (b.servingQuantity || 0) - (a.servingQuantity || 0);
            case 'favorite': {
              const aFav = isItemFavorite(a) ? 1 : 0;
              const bFav = isItemFavorite(b) ? 1 : 0;
              return bFav - aFav;
            }
            default:
              return 0;
          }
        });
      }

      return filtered;
    });

    function select(item) {
      selectedIngredient.value = item;
    }

    function isItemFavorite(item) {
      if(item.isBaseIngredient) {
        return authStore.userInfos?.favoriteBaseIngredients?.includes(item.id);
      } else {
        return item.favorite;
      }
    }

    function confirmDelete(item = null) {
      // Si item est fourni (depuis le tableau), utiliser celui-ci
      // Sinon utiliser selectedIngredient (depuis le panneau latéral)
      const target = item || selectedIngredient.value;
      if (!target) return;
      
      ingredientToDelete.value = target;
      deleteDialog.value = true;
    }

    async function deleteSelectedIngredient() {
      if (!ingredientToDelete.value || !authStore.userid) {
        deleteDialog.value = false;
        return;
      }

      try {
        const result = await deleteIngredientFromUserCollection(
          authStore.userid,
          ingredientToDelete.value.id
        );

        // Supprimer de la liste locale
        ingredients.value = ingredients.value.filter(
          (item) => item.id !== ingredientToDelete.value.id
        );

        // Si l'ingrédient supprimé était sélectionné, le désélectionner
        if (selectedIngredient.value?.id === ingredientToDelete.value.id) {
          selectedIngredient.value = null;
        }

        // Message de succès
        let message = "Ingrédient supprimé avec succès.";
        if (result.deletedRecipes > 0) {
          message += ` ${result.deletedRecipes} recette(s) contenant cet ingrédient ont également été supprimée(s).`;
        }

        snackbar.value = { 
          show: true, 
          text: message, 
          color: "success" 
        };

        ingredientToDelete.value = null;
      } catch (err) {
        console.error("❌ Erreur suppression :", err);
        errorMessage.value = "Erreur lors de la suppression de l'ingrédient.";
        snackbar.value = { 
          show: true, 
          text: errorMessage.value, 
          color: "error" 
        };
      } finally {
        deleteDialog.value = false;
      }
    }

    async function toggleFavorite(selectedIngredient) {
      if (!selectedIngredient || !authStore.userid) return;

      try {
        const id = selectedIngredient.id;
        const current = ingredients.value.find((i) => i.id === id);
        if (!current) return;

        const newValue = !current.favorite;

        await setIngredientAsFavorite(authStore.userid, id, newValue);
        
        current.favorite = newValue;
        selectedIngredient.favorite = newValue;
      } catch (err) {
        console.error("❌ Erreur favoris :", err);
        errorMessage.value = "Erreur lors de la mise à jour du favori.";
        snackbar.value = { show: true, text: errorMessage.value, color: "error" };
      }
    }

    async function onIngredientSaved(updated) {
      if (!updated || !authStore.userid) return;

      try {
        const id = updated.id;
        const payload = { ...updated };
        delete payload.source;

        await updateProduct(authStore.userid, { [id]: payload });

        const idx = ingredients.value.findIndex((i) => i.id === id);
        if (idx !== -1) {
          ingredients.value[idx] = { ...ingredients.value[idx], ...payload };
        }
        selectedIngredient.value = { ...selectedIngredient.value, ...payload };
        
        snackbar.value = { show: true, text: "Ingrédient modifié avec succès.", color: "success" };
      } catch (err) {
        console.error('Erreur sauvegarde ingrédient :', err);
        errorMessage.value = 'Erreur lors de la sauvegarde de l\'ingrédient.';
        snackbar.value = { show: true, text: errorMessage.value, color: "error" };
      }
    }

    onMounted(() => {
      watch(
        () => authStore.userInfos,
        async () => {
          if (!authStore?.userid) return;
          try {
            isLoadingIngredients.value = true;

            const userData = await getUserIngredients(authStore.userid);

            ingredients.value = Object.entries(userData).map(([id, data]) => ({
              id,
              ...data,
              source: "user",
            }));

          } catch (error) {
            console.error("Erreur récupération ingrédients:", error);
            errorMessage.value = "Erreur lors du chargement des ingrédients.";
          } finally {
            isLoadingIngredients.value = false;
          }

        },
        { immediate: true }
      );
    });

    return {
      selectedCategory,
      categories,
      authStore,
      errorMessage,
      snackbar,
      router,
      ingredients,
      selectedIngredient,
      select,
      searchQuery,
      filteredIngredients,
      isLoadingIngredients,
      confirmDelete,
      deleteSelectedIngredient,
      toggleFavorite,
      isItemFavorite,
      editModalOpen,
      onIngredientSaved,
      deleteDialog,
      ingredientToDelete,
      sortBy,
      sortKey,
      sortDir,
      setSort,
      nutriscoreColor,
      getKcal,
      categoryCards,
      getSeasonIcon,
      seasonFilter,
    };
  },
};
</script>

<template>
  <loading-element v-if="authStore.isLoading" />
  <div class="main bg-primary" v-else>
    <div class="rowSpaceBetween fullWidth mb-5">
      <TopDeck
        v-if="authStore.userInfos"
        firstElement="Mes"
        secondElement="ingrédients"
        :coloredSecond="true"
      />
      <div class="rowCustom">
        <ThemeToggle />
        <AccountToggle />
      </div>
    </div>

    <div class="mi-banner rounded-xl mb-6" @click="router.push('/browse_base_ingredients')">
      <div>
        <h2 class="mi-banner__title">Pour bien commencer</h2>
        <p class="mi-banner__sub">Parcourez une sélection d'ingrédients de base à ajouter à votre collection, comme des épices ou des produits frais.</p>
      </div>
      <button class="mi-banner__btn">
        <v-icon size="15">mdi-food-apple</v-icon>
        Parcourir
      </button>
    </div>

    <v-row class="mt-2" no-gutters>
      <!-- Liste d'ingrédients -->
      <v-col cols="12" md="9" class="pr-2">

        <div class="pa-4 bg-secondary rounded-xl">
          <!-- Header : titre + count -->
          <div class="mi-list-header">
            <div>
              <span class="mi-list-title">Mes ingrédients</span>
              <span class="mi-list-count">{{ filteredIngredients.length }}</span>
            </div>
          </div>

          <!-- Cards catégories -->
          <div class="mi-category-cards">
            <div
              v-for="cat in categoryCards"
              :key="cat.key"
              class="mi-cat-card"
              :class="{ 'mi-cat-card--active': selectedCategory === cat.key }"
              @click="selectedCategory = selectedCategory === cat.key ? 'Tous' : cat.key; selectedIngredient = null"
            >
              <div class="mi-cat-card__fallback">
                <span class="mi-cat-card__emoji">{{ cat.emoji }}</span>
              </div>
              <div class="mi-cat-card__overlay">
                <span class="mi-cat-card__label">{{ cat.label }}</span>
                <span class="mi-cat-card__count">{{ cat.count }}</span>
              </div>
            </div>
          </div>

          <!-- Barre de recherche + bouton ajouter -->
          <div class="mi-search-row mb-3">
            <v-text-field
              v-model="searchQuery"
              placeholder="Rechercher un ingrédient…"
              hide-details
              density="compact"
              variant="tonal"
              prepend-inner-icon="mdi-magnify"
              class="searchField rounded-lg bg-tertiary"
              rounded="lg"
            />
            <button class="mi-btn-add-sq" @click="router.push('/add_ingredient')">
              <v-icon size="16">mdi-plus</v-icon>
            </button>
          </div>

          <!-- Filtres saisonnalité -->
          <div class="season-filters mb-4">
            <button
              class="season-filter-btn"
              :class="{ 'season-filter-btn--active': seasonFilter === 'winter' }"
              @click="seasonFilter = seasonFilter === 'winter' ? null : 'winter'"
            >❄️ Hiver</button>
            <button
              class="season-filter-btn"
              :class="{ 'season-filter-btn--active': seasonFilter === 'summer' }"
              @click="seasonFilter = seasonFilter === 'summer' ? null : 'summer'"
            >☀️ Été</button>
            <button
              class="season-filter-btn season-filter-btn--frozen"
              :class="{ 'season-filter-btn--active': seasonFilter === 'frozen' }"
              @click="seasonFilter = seasonFilter === 'frozen' ? null : 'frozen'"
            >🧊 Surgelé</button>
          </div>

          <!-- Loader -->
          <div v-if="isLoadingIngredients" class="d-flex justify-center my-10">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <div v-else>
            <!-- Tableau -->
            <v-table density="compact" class="bg-tertiary rounded-lg">
              <thead>
                <tr>
                  <th style="width:20px;"></th>
                  <th style="width:48px;"></th>
                  <th class="mi-th-sortable" @click="setSort('name')">
                    Nom
                    <span class="mi-sort-arrow">{{ sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                  </th>
                  <th class="mi-th-sortable" @click="setSort('category')">
                    Catégorie
                    <span class="mi-sort-arrow">{{ sortKey === 'category' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                  </th>
                  <th class="mi-th-sortable" @click="setSort('nutriscore')">
                    Nutriscore
                    <span class="mi-sort-arrow">{{ sortKey === 'nutriscore' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                  </th>
                  <th class="mi-th-sortable" @click="setSort('kcal')">
                    Kcal/100g
                    <span class="mi-sort-arrow">{{ sortKey === 'kcal' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                  </th>
                  <th style="width:52px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in filteredIngredients"
                  :key="item.id"
                  class="ingredients ingredient-row"
                  @click="select(item)"
                >
                  <td @click.stop>
                    <v-icon
                      :color="isItemFavorite(item) ? 'orange' : 'grey-lighten-2'"
                      size="16"
                      @click.stop="toggleFavorite(item)"
                      style="cursor:pointer;"
                    >{{ isItemFavorite(item) ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                  </td>
                  <td class="ingredient-cell">
                    <div class="ingredient-content">
                      <v-img
                        v-if="item.productImage"
                        :src="item.productImage"
                        width="32"
                        height="32"
                        cover
                        class="rounded"
                      />
                      <v-icon v-else color="grey-lighten-1" size="32">mdi-food</v-icon>
                    </div>
                  </td>
                  <td class="font-weight-medium">{{ item.productName }}</td>
                  <td>
                    <span class="text-caption text-medium-emphasis">{{ item.category }}</span>
                  </td>
                  <td>
                    <v-chip
                      v-if="item.nutriscore"
                      :color="nutriscoreColor(item.nutriscore)"
                      size="x-small"
                      label
                      class="font-weight-bold text-white px-2"
                    >{{ item.nutriscore }}</v-chip>
                    <span v-else class="text-caption text-medium-emphasis">—</span>
                  </td>
                  <td>
                    <span class="text-caption font-weight-medium">{{ getKcal(item) }}</span>
                  </td>
                  <td @click.stop>
                    <v-btn
                      v-if="!item.isEditingDisabled"
                      icon
                      size="small"
                      variant="text"
                      color="error"
                      @click.stop="confirmDelete(item)"
                    >
                      <v-icon size="18">mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </div>
      </v-col>
      
      <!-- Edit modal component -->
      <EditIngredientModal v-model="editModalOpen" :ingredient="selectedIngredient" @saved="onIngredientSaved" />

      <!-- Fiche ingrédient -->
      <v-col cols="12" md="3" class="bg-secondary pa-4 rounded-xl">
        <div v-if="selectedIngredient">
          <div class="d-flex align-start justify-space-between mb-4">
            <!-- Nom du produit -->
            <h1 class="font-weight-bold text-h5 mb-0">
              {{ selectedIngredient.productName }}
            </h1>

            <v-img
              :src="selectedIngredient.productImage"
              width="100"
              max-height="100"
              class="ml-4 rounded"
              contain
              v-if="selectedIngredient.productImage"
            />
            <v-icon v-else color="grey lighten-1" size="100" class="ml-4"
              >mdi-food-apple-outline</v-icon
            >
          </div>

          <div v-if="selectedIngredient.nutriments_map">
            <h4 class="mt-4">Nutriments</h4>
            <ul class="nutriment-list">
              <p
                v-for="(item, index) in selectedIngredient.nutriments_map"
                :key="index"
                class="nutriment-line"
              >
                <span>{{ $t(item.key) }}</span>
                <span>{{ item.value }}</span>
              </p>
            </ul>
          </div>

          <div
            v-if="
              selectedIngredient.allergens &&
              selectedIngredient.allergens.length
            "
          >
            <p class="mb-2 font-weight-medium">Allergènes :</p>
            <div class="allergen-list">
              <v-chip
                v-for="(allergen, index) in selectedIngredient.allergens"
                :key="index"
                color="blue lighten-2"
                text-color="white"
                class="ma-1"
                label
                small
              >
                {{ $t(allergen) }}
              </v-chip>
            </div>
          </div>
          <div v-else>
            <p class="text-grey">Aucun allergène</p>
          </div>

          <p class="text-red mt-2" v-if="selectedIngredient.novaGroup?.string">
            <v-chip :color="selectedIngredient.novaGroup.color">
              {{ $t(selectedIngredient.novaGroup.string) }}
            </v-chip>
          </p>
          <div class="ingredientActions">
            <div class="ingredientActions__title">Actions</div>
            <v-btn
              block
              class="ingredientActionBtn"
              rounded="lg"
              elevation="0"
              color="accent"
              variant="tonal"
              @click="editModalOpen = true"
            >
              <v-icon start size="18">mdi-pencil</v-icon>
              Modifier l'ingrédient
            </v-btn>
            <v-btn
              v-if="!selectedIngredient.isEditingDisabled"
              block
              color="error"
              class="ingredientActionBtn"
              rounded="lg"
              elevation="0"
              variant="tonal"
              @click="confirmDelete()"
            >
              <v-icon start size="18">mdi-delete</v-icon>
              Supprimer
            </v-btn>

            <v-btn
              block
              class="ingredientActionBtn"
              :color="isItemFavorite(selectedIngredient) ? 'orange' : 'purple'"
              rounded="lg"
              variant="tonal"
              @click="toggleFavorite(selectedIngredient)"
            >
              <v-icon start size="18">
                {{ isItemFavorite(selectedIngredient) ? 'mdi-star' : 'mdi-star-outline' }}
              </v-icon>
              {{
                isItemFavorite(selectedIngredient)
                  ? "Retirer des favoris"
                  : "Mettre en favoris"
              }}
            </v-btn>
          </div>

        </div>
      </v-col>
    </v-row>

    <!-- Dialog de confirmation de suppression -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5 font-weight-bold">
          ⚠️ Confirmer la suppression
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            Êtes-vous sûr de vouloir supprimer <strong>{{ ingredientToDelete?.productName }}</strong> ?
          </p>
          <v-alert type="warning" variant="tonal" class="mb-0">
            <strong>Attention :</strong> Toutes les recettes contenant cet ingrédient seront également supprimées.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="deleteDialog = false"
          >
            Annuler
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="deleteSelectedIngredient"
          >
            Supprimer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" top right>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<style scoped>
/* ── Header liste ─────────────────────────────── */
.mi-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.mi-list-title {
  font-size: 15px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}
.mi-list-count {
  font-size: 12px;
  color: rgba(var(--v-theme-text), 0.35);
  margin-left: 8px;
}
.mi-btn-add {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #1A1615;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;
  white-space: nowrap;
}
.mi-btn-add:hover { opacity: 0.85; }

/* ── Category cards ───────────────────────────── */
.mi-category-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}
.mi-cat-card {
  position: relative;
  width: 90px;
  height: 90px;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  border: 2px solid transparent;
  flex-shrink: 0;
}
.mi-cat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(117,83,252,0.18);
}
.mi-cat-card--active {
  border-color: #7553FC;
  box-shadow: 0 0 0 3px rgba(117,83,252,0.22);
}
.mi-cat-card__fallback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0edfb, #e8e3f5);
}
.mi-cat-card__emoji { font-size: 30px; line-height: 1; }
.mi-cat-card__overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 18px 7px 7px;
  background: linear-gradient(to top, rgba(0,0,0,0.68) 0%, transparent 100%);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
}
.mi-cat-card__label {
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0,0,0,0.4);
  line-height: 1.2;
}
.mi-cat-card__count {
  font-size: 9px;
  font-weight: 600;
  color: rgba(255,255,255,0.8);
  display: none;
}
.mi-cat-card--active .mi-cat-card__count { display: inline; }

/* ── Search row ───────────────────────────────── */
.mi-search-row { display: flex; align-items: center; gap: 10px; }
.searchField { flex: 1; }

.mi-btn-add-sq {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: #1A1615;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;
}
.mi-btn-add-sq:hover { opacity: 0.85; }


.mi-th-sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.mi-th-sortable:hover { color: rgb(var(--v-theme-accent)); }
.mi-sort-arrow {
  font-size: 10px;
  margin-left: 4px;
  opacity: 0.45;
}
.mi-th-sortable:hover .mi-sort-arrow { opacity: 0.9; }


.addIngredientBanner {
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-accent)),
    rgba(255, 255, 255, 0.6)
  );
  border-radius: 8px;
  color: white;
  margin-bottom: 1.5rem;
}

.nutriment-line {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.ingredients:hover {
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.05);
}

.ingredient-row {
  min-height: 56px;
  height: 56px;
}

.ingredient-cell {
  vertical-align: middle !important;
  height: 56px;
}

.ingredient-content {
  display: flex;
  align-items: center;
  height: 100%;
}

.delete-icon-btn {
  opacity: 0.7;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.delete-icon-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}

.ingredientActions {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid rgba(var(--v-theme-text), 0.08);
}

.ingredientActions__title {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-text), 0.6);
}

.ingredientActionBtn {
  text-transform: none;
  font-weight: 600;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.14);
}

.rowCustom {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

.mi-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, rgb(var(--v-theme-accent)), rgba(255,255,255,0.55));
  color: white;
  cursor: pointer;
  gap: 12px;
}
.mi-banner__title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 4px;
}
.mi-banner__sub {
  font-size: 12px;
  margin: 0;
  opacity: 0.85;
}
.mi-banner__btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: none;
  border-radius: 999px;
  color: rgb(var(--v-theme-accent));
  font-size: 12px;
  font-weight: 600;
  padding: 7px 16px;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  transition: opacity 0.15s;
  flex-shrink: 0;
}
.mi-banner__btn:hover { opacity: 0.88; }

.browseBaseRecipesBanner {
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-accent)),
    rgba(255, 255, 255, 0.6)
  );
  color: white;
  margin-bottom: 1.5rem;
}

.season-icon { margin-left: 5px; font-size: 13px; vertical-align: middle; }

.season-filters { display: flex; gap: 8px; flex-wrap: wrap; }
.season-filter-btn {
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1.5px solid rgba(0,0,0,0.12);
  background: transparent;
  color: rgba(0,0,0,0.55);
  cursor: pointer;
  transition: all 0.15s;
}
.season-filter-btn:hover { border-color: #7553FC; color: #7553FC; }
.season-filter-btn--active { background: #7553FC; border-color: #7553FC; color: #fff; font-weight: 600; }
.season-filter-btn--frozen.season-filter-btn--active { background: #e1f5fe; border-color: #4fc3f7; color: #0288d1; }
</style>
